import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../core/api-client';
import { submitContact } from '../api';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { MarketingPageHead } from '../MarketingPageHead';

const TOPICS = [
  { value: 'general',   label: 'General question' },
  { value: 'billing',   label: 'Billing' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'feature',   label: 'Feature request' },
  { value: 'demo',      label: 'Book a demo' },
] as const;

const DEMO_SUBJECT = 'I\'d like to book a demo';

export function ContactPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('topic') === 'demo';

  const [name,    setName]    = React.useState('');
  const [email,   setEmail]   = React.useState('');
  const [subject, setSubject] = React.useState(isDemo ? DEMO_SUBJECT : '');
  const [topic,   setTopic]   = React.useState<string>(isDemo ? 'demo' : 'general');
  const [message, setMessage] = React.useState('');
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error,      setError]      = React.useState<string | null>(null);
  const [success,    setSuccess]    = React.useState(false);

  const widgetEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitContact({ name: name.trim(), email: email.trim(), subject: subject.trim(), topic, message: message.trim(), turnstileToken: turnstileToken || undefined });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(String((err as any)?.message ?? err));
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section className="min-h-[80vh] flex items-center px-6 py-20">
        <div className="max-w-md mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white/75 dark:bg-white/[0.04] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card dark:shadow-none p-8 text-center"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-lime-neon flex items-center justify-center mb-5 shadow-pill">
              <Check className="h-6 w-6 text-ink" strokeWidth={3} />
            </div>
            <h1 className="font-light text-display-md text-ink dark:text-white leading-tight tracking-[-0.025em]">
              Message received.
            </h1>
            <p className="mt-4 text-[14.5px] text-ink/65 dark:text-white/65 leading-relaxed">
              Thanks — we've got your message and emailed you a confirmation. We usually reply within a day.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[80vh] py-16 lg:py-24 px-6">
      <MarketingPageHead
        title={isDemo ? 'Book a demo' : 'Contact support'}
        description={
          isDemo
            ? 'Tell us a bit about your team and we\'ll set up a walkthrough of Worqly.'
            : 'We usually reply within a day. Reach out with any question, billing issue, or feature idea.'
        }
      />
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-coral mb-3">
            {isDemo ? 'Book a demo' : 'Contact support'}
          </p>
          <h1 className="font-bold text-hero-lg text-ink dark:text-white leading-[1.02] tracking-[-0.03em] text-balance">
            {isDemo ? (
              <>See Worqly <span className="italic text-coral">live.</span></>
            ) : (
              <>We're here to <span className="italic text-coral">help.</span></>
            )}
          </h1>
          <p className="mt-6 text-body-lg leading-relaxed text-ink/65 dark:text-white/65 text-pretty">
            {isDemo
              ? 'Tell us about your team and what you\'re running today — we\'ll walk you through the modules that fit, live.'
              : 'We usually reply within a day. Reach out with any question, billing issue, or feature idea.'}
          </p>
          <ul className="mt-7 space-y-3 text-[14px] text-ink/85 dark:text-white/80">
            {['Fast, friendly replies', 'Billing & account help', 'Technical troubleshooting', 'Feature requests welcome'].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-coral mt-[2px]" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={onSubmit}
            className="rounded-3xl bg-white/75 dark:bg-white/[0.04] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card dark:shadow-none p-7 lg:p-9 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                  autoFocus
                  autoComplete="name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@acme.co"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic">Topic</Label>
              <select
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-[13.5px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {TOPICS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question or issue in detail…"
                required
                rows={5}
                className="resize-none"
              />
            </div>

            {error ? (
              <p className="text-[13px] text-destructive">{error}</p>
            ) : null}

            <TurnstileWidget onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={
                submitting ||
                !name.trim() || !email.trim() || !subject.trim() || !message.trim() ||
                (widgetEnabled && !turnstileToken)
              }
            >
              {submitting ? 'Sending…' : (
                <>
                  Send message
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-[12px] text-ink/55 dark:text-white/55 leading-relaxed">
              We'll reply to the email address above. Usually within one business day.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
